/**
 * Зелёная плашка «Вернуться к звонку · 02:14» над всеми экранами, пока звонок свёрнут
 * (аудит удобства 2026-10-04, P0-5) — как у Telegram и WhatsApp. Нажатие открывает экран звонка.
 *
 * <p>Видна, только пока звонок жив и его экран закрыт: на самом экране звонка она не нужна, а
 * после конца звонка исчезает вместе с сессией.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useLocale } from '../i18n/LocaleContext';
import { formatCallDuration, useCallDurationSince } from './callDuration';
import { useAttachedCallScreenKey, useCallSessionLive, useCallSessionMeta } from './callSessionHooks';
import type { VoiceCallApi } from './CallSessionRunner';
import { returnToActiveCall } from './startDirectCall';

/** Высота самой плашки, без отступа под строку состояния. */
export const RETURN_TO_CALL_BAR_HEIGHT = 36;

export function ReturnToCallBar(): React.ReactElement | null {
  const meta = useCallSessionMeta();
  const live = useCallSessionLive<VoiceCallApi, unknown>();
  const attachedKey = useAttachedCallScreenKey();
  const insets = useSafeAreaInsets();
  const { t } = useLocale();
  const sessionLive = live && meta && live.key === meta.key ? live : null;
  const vc = sessionLive?.vc ?? null;
  const visible = !!meta && attachedKey !== meta.key && !sessionLive?.ended;
  const phase = vc?.phase ?? 'idle';
  const seconds = useCallDurationSince(visible && phase === 'incall' ? vc?.incallSince ?? null : null);
  if (!visible || !meta) return null;

  const status =
    phase === 'incall'
      ? formatCallDuration(seconds)
      : phase === 'ringing'
        ? t('callIncomingFull')
        : t('callPhaseCalling');
  return (
    <Pressable
      onPress={returnToActiveCall}
      accessibilityRole="button"
      accessibilityLabel={`${t('callReturnBar')}: ${meta.params.title}`}
      accessibilityHint={t('callReturnBarHint')}
      style={({ pressed }) => [styles.bar, { paddingTop: insets.top }, pressed && styles.pressed]}>
      <View style={styles.row}>
        <View style={styles.dot} />
        <Text style={styles.text} numberOfLines={1}>
          {t('callReturnBar')} · {status}
        </Text>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  bar: { backgroundColor: '#16a34a' },
  pressed: { backgroundColor: '#15803d' },
  row: {
    height: RETURN_TO_CALL_BAR_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 16,
  },
  dot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#dcfce7' },
  text: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    // Часы на моноширинных цифрах: иначе строка дёргается по ширине каждую секунду.
    fontVariant: ['tabular-nums'],
  },
});
